import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { leaveAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { format } from 'date-fns';
import { 
  Calendar as CalendarIcon, 
  Plus, 
  Clock, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  Plane,
  HeartPulse,
  Baby,
  Home
} from 'lucide-react';
import { toast } from 'sonner';

const Leave = () => {
  const { user } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    leave_type: '',
    start_date: null,
    end_date: null,
    reason: ''
  });

  useEffect(() => {
    fetchLeaves();
  }, [user?.employee_id]);

  const fetchLeaves = async () => {
    try {
      setLoading(true);
      const response = await leaveAPI.getAll();
      setLeaves(response.data);
    } catch (error) {
      console.error('Leave fetch error:', error);
      toast.error('Failed to load leave requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.leave_type || !formData.start_date || !formData.end_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await leaveAPI.create({
        leave_type: formData.leave_type,
        start_date: format(formData.start_date, 'yyyy-MM-dd'),
        end_date: format(formData.end_date, 'yyyy-MM-dd'),
        reason: formData.reason
      });
      toast.success('Leave request submitted!');
      setShowDialog(false);
      setFormData({ leave_type: '', start_date: null, end_date: null, reason: '' });
      fetchLeaves();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit leave request');
    }
  };

  const leaveTypes = [
    { value: 'annual', label: 'Annual Leave', icon: Plane, color: 'blue' },
    { value: 'sick', label: 'Sick Leave', icon: HeartPulse, color: 'red' },
    { value: 'family', label: 'Family Responsibility', icon: Baby, color: 'violet' },
    { value: 'study', label: 'Study Leave', icon: Home, color: 'amber' },
  ];

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  const getLeaveIcon = (type) => {
    const leaveType = leaveTypes.find(l => l.value === type);
    if (leaveType) {
      const Icon = leaveType.icon;
      return <Icon className={`w-5 h-5 text-${leaveType.color}-500`} />;
    }
    return <CalendarIcon className="w-5 h-5 text-slate-500" />;
  };

  const pendingCount = leaves.filter(l => l.status === 'pending').length;
  const approvedCount = leaves.filter(l => l.status === 'approved').length;
  const usedDays = leaves.filter(l => l.status === 'approved').reduce((sum, l) => {
    const start = new Date(l.start_date);
    const end = new Date(l.end_date);
    return sum + Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
  }, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-slate-500">Loading leave requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leave-page">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Leave Balance</p>
                <p className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {21 - usedDays} days
                </p>
              </div>
              <CalendarIcon className="w-10 h-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Used This Year</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {usedDays} days
                </p>
              </div>
              <CheckCircle className="w-10 h-10 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Pending Requests</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {pendingCount}
                </p>
              </div>
              <Clock className="w-10 h-10 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Approved</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {approvedCount}
                </p>
              </div>
              <CheckCircle className="w-10 h-10 text-indigo-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Leave Requests */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Leave Requests</CardTitle>
              <CardDescription>Manage your leave applications</CardDescription>
            </div>
            <Button 
              onClick={() => setShowDialog(true)}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="new-leave-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Request
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {leaves.length > 0 ? (
            <div className="space-y-4">
              {leaves.map((leave) => (
                <div 
                  key={leave.id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                  data-testid={`leave-item-${leave.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center border border-slate-200">
                      {getLeaveIcon(leave.leave_type)}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 capitalize">
                        {leave.leave_type?.replace('_', ' ')} Leave
                      </p>
                      <p className="text-sm text-slate-500">
                        {leave.start_date} to {leave.end_date}
                      </p>
                      {leave.reason && (
                        <p className="text-sm text-slate-400 mt-1">{leave.reason}</p>
                      )}
                    </div>
                  </div>
                  {getStatusBadge(leave.status)}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <CalendarIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Leave Requests</h3>
              <p className="text-slate-500 mb-4">You haven't submitted any leave requests yet</p>
              <Button 
                onClick={() => setShowDialog(true)}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Request Leave
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Leave Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>New Leave Request</DialogTitle>
            <DialogDescription>Submit a new leave application</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Leave Type</Label>
              <Select 
                value={formData.leave_type} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, leave_type: value }))}
              >
                <SelectTrigger data-testid="leave-type-select">
                  <SelectValue placeholder="Select leave type" />
                </SelectTrigger>
                <SelectContent>
                  {leaveTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon className="w-4 h-4" />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start font-normal"
                      data-testid="start-date-btn"
                    >
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {formData.start_date ? format(formData.start_date, 'PPP') : 'Pick date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.start_date}
                      onSelect={(date) => setFormData(prev => ({ ...prev, start_date: date }))}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start font-normal"
                      data-testid="end-date-btn"
                    >
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {formData.end_date ? format(formData.end_date, 'PPP') : 'Pick date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.end_date}
                      onSelect={(date) => setFormData(prev => ({ ...prev, end_date: date }))}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Reason (Optional)</Label>
              <Textarea 
                placeholder="Enter reason for leave..."
                value={formData.reason}
                onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                data-testid="leave-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="submit-leave-btn"
            >
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Leave;
